import type { Task, TaskStatus } from '@dyspatch/core';

/**
 * Mirrored from core's `hasOpenPr`, `mergeOrder` and `unmergedDependencies`
 * (packages/core/src/state-machine.ts) rather than imported, for the reason
 * `notes.ts` gives: a value import of `@dyspatch/core` pulls the barrel, and
 * with it `node:crypto`, into the browser bundle. Pinned to core's own
 * functions by `packages/hub/test/web.test.ts`.
 *
 * Issue #24: the hub never merges, so "which order may these land in" and
 * "is this one safe to merge yet" are both questions the console has to
 * answer from the record alone, without asking GitHub.
 */

/** A step counts as an open pull request once it has one and review has not closed it. */
export function hasOpenPr(task: Pick<Task, 'status' | 'prUrl'>): boolean {
  return task.status === 'review' && task.prUrl != null;
}

/**
 * The order a job's open pull requests may land in, dependencies first.
 * Self-contained rather than trusting a caller's array to already be sorted:
 * `topoOrder` in core is the source of truth and this is its whole algorithm,
 * restricted to the steps still open.
 */
export function mergeOrder<T extends Pick<Task, 'key' | 'dependsOn' | 'status' | 'prUrl'>>(
  tasks: readonly T[],
): T[] {
  const open = tasks.filter(hasOpenPr);
  const byKey = new Map(open.map((t) => [t.key, t]));
  const remaining = new Set(byKey.keys());
  const out: T[] = [];

  while (remaining.size > 0) {
    const ready = [...remaining].filter((key) =>
      byKey.get(key)!.dependsOn.every((d) => !remaining.has(d)),
    );
    if (ready.length === 0) return [...open]; // cycle; cannot happen, validateGraph forbids one
    ready.sort();
    for (const key of ready) {
      out.push(byKey.get(key)!);
      remaining.delete(key);
    }
  }
  return out;
}

/**
 * Which of a step's dependencies have not landed on the base branch yet, by
 * key. Stricter than the scheduler's own `dependenciesMet`: a dependency
 * merely in review is enough to unblock a worker, but not enough to merge
 * ahead of.
 */
export function unmergedDependencies(
  dependsOn: readonly string[],
  statusByKey: ReadonlyMap<string, TaskStatus>,
): string[] {
  return dependsOn.filter((key) => statusByKey.get(key) !== 'done');
}
